import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel
} from "@material-ui/core";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { StripeCardNumberElement } from "@stripe/stripe-js";
import { toast } from "react-toastify";
import useStyles from "./PaymentForm.styles";
import { CARD_ELEMENT_OPTIONS } from "../../checkoutPage/components/TabPayDeposit/paymentConstants";
import { useBreakpoint } from "../../../hooks/useBreakpoint";

interface PaymentCardDetailsProps {
  amount: number;
  clientSecret: string;
  onSuccess: () => void;
  onBack?: () => void;
}

type CardError = {
  type: "validation_error";
  code: string;
  message: string;
};

const PaymentCardDetails: React.FC<PaymentCardDetailsProps> = ({ amount, clientSecret, onSuccess, onBack }) => {
  const classes = useStyles();
  const { isMoreThanMd } = useBreakpoint();
  const stripe = useStripe();
  const elements = useElements();

  const [cardNumberError, setCardNumberError] = useState<CardError>();
  const [expireDateError, setExpireDateError] = useState<CardError>();
  const [CVVError, setCVVError] = useState<CardError>();
  const [nameOnCard, setNameOnCard] = useState<string>("");
  const [nameOnCardError, setNameOnCardError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (
      !nameOnCard ||
      !!(elements.getElement(CardNumberElement) as any)?._empty ||
      !!(elements.getElement("cardExpiry") as any)?._empty ||
      !!(elements.getElement("cardCvc") as any)?._empty
    ) {
      if (!nameOnCard) setNameOnCardError("Name on card is incomplete.");
      if (!!(elements.getElement(CardNumberElement) as any)?._empty)
        setCardNumberError({ message: "Your card number is incomplete.", type: "validation_error", code: "" });
      if (!!(elements.getElement("cardExpiry") as any)?._empty)
        setExpireDateError({
          message: "Your card's expiration date is incomplete.",
          type: "validation_error",
          code: ""
        });
      if (!!(elements.getElement("cardCvc") as any)?._empty)
        setCVVError({ message: "Your card's security code is incomplete.", type: "validation_error", code: "" });
      return;
    }
    if (cardNumberError || expireDateError || CVVError || nameOnCardError) return;
    try {
      setSubmitting(true);
      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement) as StripeCardNumberElement,
          billing_details: { name: nameOnCard }
        },
        setup_future_usage: "on_session"
      });
      setSubmitting(false);
      if (payload.error) {
        toast.error(payload.error.message);
        return;
      }
      onSuccess();
    } catch (error) {
      toast.error(error as string);
      setSubmitting(false);
    }
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <Typography variant="subtitle1" gutterBottom>
        Enter your card details to pay <b>€{amount}</b>
      </Typography>
      <Box className={classes.formContainer}>
        <FormControl className={classes.formItem}>
          <FormLabel>Card information</FormLabel>
          <Box
            className={`${classes.formItemCard} ${cardNumberError || expireDateError || CVVError ? "form-error" : ""}`}
          >
            <CardNumberElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={(e) => setCardNumberError(e.error)}
              className="card-number"
            />
            <Box display={"flex"} alignItems={"center"} width={"100%"} justifyContent={"space-between"}>
              <CardExpiryElement
                options={CARD_ELEMENT_OPTIONS}
                onChange={(e) => setExpireDateError(e.error)}
                className="card-expiry"
              />
              <CardCvcElement
                options={CARD_ELEMENT_OPTIONS}
                onChange={(e) => setCVVError(e.error)}
                className="card-cvc"
              />
              <Box
                position={"absolute"}
                display={"flex"}
                top={isMoreThanMd ? "27px" : "25px"}
                right={"18px"}
                alignItems={"center"}
              >
                <img src="images/checkout/visa.svg" alt="visa" width={"30px"} />
                <img src="images/checkout/mastercard.svg" alt="mastercard" width={"30px"} />
                <img src="images/checkout/american-express.svg" alt="american-express" width={"30px"} />
              </Box>
            </Box>
          </Box>
          {(cardNumberError || expireDateError || CVVError) && (
            <FormHelperText id="card_error" style={{ color: "#eb1c26" }} color="#eb1c26">
              {cardNumberError?.message || expireDateError?.message || CVVError?.message}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl className={`${classes.formItem} ${nameOnCardError ? "form-error" : ""}`}>
          <FormLabel htmlFor="name">Name on card</FormLabel>
          <TextField
            id="name"
            variant="outlined"
            value={nameOnCard}
            onChange={(e) => {
              setNameOnCard(e.target.value);
              setNameOnCardError(undefined);
            }}
            aria-describedby="Name on card"
          />
          {nameOnCardError && (
            <FormHelperText id="my-helper-text" style={{ color: "#eb1c26" }} color="#eb1c26">
              {nameOnCardError}
            </FormHelperText>
          )}
        </FormControl>
      </Box>
      <Box display="flex" justifyContent="space-between" mt={2}>
        {onBack && (
          <Button variant="outlined" color="primary" onClick={onBack} disabled={submitting}>
            Back
          </Button>
        )}
        <Button type="submit" variant="contained" color="secondary" disabled={submitting}>
          Pay €{amount}
        </Button>
      </Box>
      {submitting && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          bgcolor="rgba(255,255,255,0.5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={2}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}
    </form>
  );
};

export default PaymentCardDetails;
